import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Menu, MenuItem, IconButton, Snackbar, Alert } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import { useAuth } from '../../../context/AuthContext';

/**
 * Composant pour changer la langue de l'application
 * Sauvegarde automatiquement la préférence de langue de l'utilisateur connecté
 */
const LanguageSwitcher = ({ variant = 'icon' }) => {
  const { i18n, t } = useTranslation();
  const { isAuthenticated, updateUserPreferences } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [snackbar, setSnackbar] = React.useState({ open: false, severity: 'success' });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const changeLanguage = async (lng) => {
    // Changer la langue de l'interface
    await i18n.changeLanguage(lng);
    handleClose();

    // Si l'utilisateur est connecté, sauvegarder automatiquement sa préférence
    if (isAuthenticated && updateUserPreferences) {
      try {
        const result = await updateUserPreferences({
          language: lng,
          display: {
            language: lng
          }
        });

        if (result.success) {
          // Notification de succès discrète (optionnel)
          console.log(`Préférence de langue sauvegardée : ${lng}`);
          setSnackbar({
            open: true,
            severity: 'success'
          });
        } else {
          console.warn('Échec de la sauvegarde de la préférence de langue:', result.message);
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde de la préférence de langue:', error);
        // Ne pas afficher d'erreur à l'utilisateur car le changement de langue a fonctionné
      }
    }
  };

  // Obtenir le nom de la langue actuelle
  const getCurrentLanguageName = () => {
    switch (i18n.language) {
      case 'fr':
        return 'Français';
      case 'en':
        return 'English';
      case 'ar':
        return 'العربية'; // Nom de la langue arabe
      default:
        return 'Français'; // Langue par défaut
    }
  };

  return (
    <>
      {variant === 'button' ? (
        <Button
          onClick={handleClick}
          startIcon={<LanguageIcon />}
          color="inherit"
        >
          {getCurrentLanguageName()}
        </Button>
      ) : (
        <IconButton
          color="inherit"
          onClick={handleClick}
          aria-label="change language"
        >
          <LanguageIcon />
        </IconButton>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {/* Option pour l'arabe */}
        <MenuItem
          onClick={() => changeLanguage('ar')}
          selected={i18n.language === 'ar'}
        >
          العربية
        </MenuItem>
        
        {/* Option pour le français */}
        <MenuItem
          onClick={() => changeLanguage('fr')}
          selected={i18n.language === 'fr'}
        >
          Français
        </MenuItem>

        {/* Option pour l'anglais */}
        <MenuItem
          onClick={() => changeLanguage('en')}
          selected={i18n.language === 'en'}
        >
          English
        </MenuItem>

        
      </Menu>

      {/* Notification discrète de sauvegarde (optionnel) */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {t('languageSwitcher.languageChanged')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LanguageSwitcher;